import { Users, UserCheck, UserX, Repeat, Activity, Percent, Timer } from 'lucide-preact'
import { memo } from 'preact/compat'
import type { AnalyticsMetricDto } from '@/types/analytics'
import { AnalyticsStatTile } from '../AnalyticsStatTile'

interface Props {
  metric: AnalyticsMetricDto | null
  loading: boolean
  error: string | null
}

// Richer per-level visitor metrics (ORB-373), shared by the Campaign and Workspace
// dashboards. Each is a thin label/icon wrapper around the shared AnalyticsStatTile so
// values, growth pills, and loading/error states stay consistent with the P2 tiles.

const iconClass = 'w-5 h-5 text-primary-600 dark:text-primary-400'

export const UniqueVisitorsStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Unique</span><span className="block">Visitors</span></>}
    icon={<Users className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
  />
))

export const KnownVisitorsStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Known</span><span className="block">Visitors</span></>}
    icon={<UserCheck className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
  />
))

export const UnknownVisitorsStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Unknown</span><span className="block">Visitors</span></>}
    icon={<UserX className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
  />
))

export const ReturningVisitorsStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Returning</span><span className="block">Visitors</span></>}
    icon={<Repeat className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
  />
))

export const SessionsStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<span className="block">Sessions</span>}
    icon={<Activity className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
  />
))

export const ConversionRateStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Conversion</span><span className="block">Rate</span></>}
    icon={<Percent className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
    format="percent"
  />
))

export const AvgSessionDurationStatTile = memo(({ metric, loading, error }: Props) => (
  <AnalyticsStatTile
    title={<><span className="block">Avg Session</span><span className="block">Duration</span></>}
    icon={<Timer className={iconClass} />}
    metric={metric}
    loading={loading}
    error={error}
    format="duration"
  />
))
